import { Link, useLocation } from "react-router-dom"
import { cn } from "@/lib/utils"
import {
  LayoutDashboard,
  FileText,
  Settings,
  Instagram,
  Users,
  UserCircle,
} from "lucide-react"
import { useQuery } from "@tanstack/react-query"
import { supabase } from "@/integrations/supabase/client"

interface SidebarNavigationProps {
  onNavigate?: () => void
}

export function SidebarNavigation({ onNavigate }: SidebarNavigationProps) {
  const location = useLocation()

  const { data: profile } = useQuery({
    queryKey: ["profile"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error("User not found")

      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", user.id)
        .single()

      if (error) throw error
      return data
    },
  })

  const isActive = (path: string) => {
    return location.pathname === path
  }

  // Links comuns para todos os usuários - Perfil agora é o primeiro item
  const commonLinks = [
    {
      href: "/profile",
      label: "Perfil",
      icon: UserCircle,
    },
    {
      href: "/",
      label: "Dashboard",
      icon: LayoutDashboard,
    },
    {
      href: "/documents",
      label: "Documentos",
      icon: FileText,
    },
  ]

  // Adiciona o link do Instagram apenas se o usuário tiver permissão
  if (profile?.instagram) {
    commonLinks.push({
      href: "/instagram",
      label: "Instagram",
      icon: Instagram,
    })
  }

  // Links adicionais apenas para administradores
  const adminLinks = [
    {
      href: "/users",
      label: "Usuários",
      icon: Users,
    },
    {
      href: "/settings",
      label: "Configurações",
      icon: Settings,
    },
  ]

  // Determina quais links mostrar com base na função do usuário
  const links = profile?.role === 'admin' 
    ? [...commonLinks, ...adminLinks] 
    : commonLinks

  return (
    <nav className="grid gap-1 p-4">
      {links.map((link) => {
        const Icon = link.icon
        return (
          <Link
            key={link.href}
            to={link.href}
            onClick={onNavigate}
            className={cn(
              "flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-all hover:bg-accent",
              isActive(link.href)
                ? "bg-accent text-accent-foreground"
                : "text-muted-foreground hover:text-accent-foreground"
            )}
          >
            <Icon className="h-4 w-4" />
            {link.label}
          </Link>
        )
      })}
    </nav>
  )
}